import { Command, Menu, Search } from "lucide-react";
import * as React from "react";

import { cn } from "@/utils/cn";

interface InputProps extends React.ComponentProps<"input"> {
	startIcon?: React.ReactNode;
	endIcon?: React.ReactNode;
	showKeyboardShortcut?: boolean;
	keyboardShortcut?: string;
}

function Input({ className, type, startIcon, endIcon, showKeyboardShortcut, ...props }: InputProps) {
	if (startIcon || endIcon || showKeyboardShortcut) {
		return (
			<div className="relative">
				{/* Start Icon */}
				{startIcon && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{startIcon}</div>}

				<input
					type={type}
					data-slot="input"
					className={cn(
						"file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
						"focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
						"aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
						startIcon ? "pl-10" : "pl-3",
						endIcon || showKeyboardShortcut ? "pr-16" : "pr-3",
						className,
					)}
					{...props}
				/>

				{/* Keyboard Shortcut or End Icon */}
				{(endIcon || showKeyboardShortcut) && (
					<div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
						{showKeyboardShortcut && (
							<div className="flex items-center gap-1 text-xs text-muted-foreground">
								<kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
									<Command className="size-3" />
								</kbd>
								<kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
									S
								</kbd>
							</div>
						)}
						{endIcon && <div className="text-muted-foreground">{endIcon}</div>}
					</div>
				)}
			</div>
		);
	}

	return (
		<input
			type={type}
			data-slot="input"
			className={cn(
				"file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
				"focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
				"aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
				className,
			)}
			{...props}
		/>
	);
}

// Search Input - matches mood board
interface SearchInputProps extends Omit<InputProps, "type" | "startIcon"> {
	onClear?: () => void;
}

function SearchInput({ placeholder = "Search...", showKeyboardShortcut = true, endIcon, ...props }: SearchInputProps) {
	return (
		<Input
			type="search"
			placeholder={placeholder}
			startIcon={<Search className="size-4" />}
			showKeyboardShortcut={showKeyboardShortcut}
			endIcon={endIcon || <Menu className="size-4" />}
			{...props}
		/>
	);
}

export { Input, SearchInput };
